/**
 * QueuedLayerSection — one layer's queued elements, grouped into routing-rule
 * sub-sections (Default first, then rule-based). Each rule is its own drag list;
 * a within-rule drop rebuilds the FULL layer order (sections concatenated in
 * their fixed order) so the bank priorities written by useQueueReorder stay
 * dense and global — the top of the whole layer is still the NEXT UP.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useEffect, useState } from 'react';
import { ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import type { FlowLayer, QueueElement } from '@trafficpulse/shared';

import type { QueueProvider } from '@/features/trackers/flow-optimizer/queue-provider';
import { useQueueReorder } from '../../../../hooks/useQueueReorder';
import { layerIcon } from '../../queue-icons';
import { layerLabel } from '../../queue-status.util';
import type { BulkAwareProps } from '../../bulk/row/bulk-row.types';
import { groupByRule } from '../queue-rule-groups';
import { useRuleCollapse } from '../use-rule-collapse';
import QueuedRuleGroup from './QueuedRuleGroup';

export interface QueuedLayerSectionProps extends BulkAwareProps {
  layer: FlowLayer;
  /** This layer's queued elements, already in priority order. */
  elements: QueueElement[];
  incoming: Map<string, string>;
  campaignId: string;
  provider: QueueProvider;
  /**
   * The list is showing a SUBSET (a search is on), so dragging is off.
   *
   * A drop rewrites dense 1..N priorities across everything it was handed. Given
   * a filtered list that renumbers the matches over the top of the hidden rows —
   * duplicate priorities nobody asked for, and a different element promoted
   * next. Reordering needs the whole layer in view.
   */
  reorderDisabled?: boolean;
}

const QueuedLayerSection: FC<QueuedLayerSectionProps> = ({
  layer,
  elements,
  incoming,
  campaignId,
  provider,
  bulk,
  reorderDisabled = false,
}) => {
  const reorder = useQueueReorder(campaignId, provider);
  // Local order for optimistic moves; re-sync when the query returns fresh data.
  const [order, setOrder] = useState<QueueElement[]>(elements);
  useEffect(() => setOrder(elements), [elements]);

  const sections = groupByRule(order);
  // Rebuild the whole layer order (sections in their fixed order, the dragged
  // one swapped in) so priorities stay dense across rules.
  const onSectionReorder = (key: string) => (next: QueueElement[]) => {
    if (reorderDisabled) return;
    const full = sections.flatMap((s) => (s.key === key ? next : s.elements));
    setOrder(full);
    reorder.mutate({ layer, ordered: full });
  };

  const Icon = layerIcon(layer);
  const collapse = useRuleCollapse(sections.map((s) => s.key));
  const CollapseIcon = collapse.allCollapsed ? ChevronsUpDown : ChevronsDownUp;
  let rankOffset = 0;
  return (
    <div className="rounded-md border border-border/60 bg-card/30 p-2">
      <p className="mb-1.5 flex items-center gap-1.5 px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3.5 w-3.5" aria-hidden />
        {layerLabel(layer)}s
        <span className="text-muted-foreground/60">· {order.length}</span>
        {collapse.worthShowing ? (
          <button
            type="button"
            onClick={collapse.toggleAll}
            className="ml-auto inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium normal-case tracking-normal text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <CollapseIcon className="h-3.5 w-3.5" aria-hidden />
            {collapse.allCollapsed ? 'Expand all' : 'Collapse all'}
          </button>
        ) : null}
      </p>
      <div className="space-y-2">
        {sections.map((section) => {
          const offset = rankOffset;
          rankOffset += section.elements.length;
          return (
            <QueuedRuleGroup
              key={section.key}
              section={section}
              rankOffset={offset}
              incoming={incoming}
              campaignId={campaignId}
              provider={provider}
              pending={reorder.isPending || reorderDisabled}
              bulk={bulk}
              open={collapse.isOpen(section.key)}
              onToggleOpen={() => collapse.toggleOne(section.key)}
              onReorder={onSectionReorder(section.key)}
            />
          );
        })}
      </div>
    </div>
  );
};

export default QueuedLayerSection;
